import {
  buildSaveBookingExpenseInvoiceAssignmentsRequest,
  mapExpenseInvoiceDetailsToBookingExpenseInvoiceDetailsLookup,
  mapExpenseInvoiceToBookingExpenseInvoiceLookup,
  type BookingExpenseInvoiceDetailsLookup,
  type BookingExpenseInvoiceLookup,
  type SaveBookingExpenseInvoiceAssignmentsRequest,
} from './expenseInvoicesLookupMapper';

type ExpenseInvoiceDetailsLookupSource =
  Parameters<typeof mapExpenseInvoiceDetailsToBookingExpenseInvoiceDetailsLookup>[0];
type BookingExpenseInvoiceListItemSource = NonNullable<
  Parameters<typeof mapExpenseInvoiceToBookingExpenseInvoiceLookup>[0]
>;

type ExpenseInvoiceDetailsTriggerResult = {
  unwrap: () => Promise<ExpenseInvoiceDetailsLookupSource>;
};

export interface ExpenseInvoiceSuggestionForBookingResponseItem {
  expenseInvoiceId: number;
  invoiceNumber?: string | null;
  supplierName?: string | null;
  issueDate: string;
  saleDate?: string | null;
  totalGross: number;
  currency: string;
  score: number;
  confidence: string;
  reasons?: string[] | null;
}

export interface BookingExpenseInvoicesPagedResponse {
  items?: BookingExpenseInvoiceListItemSource[] | null;
}

export const mapBookingExpenseInvoicesLookupData = (
  pagedResponse: BookingExpenseInvoicesPagedResponse | undefined
): BookingExpenseInvoiceLookup[] =>
  (pagedResponse?.items ?? [])
    .map((expenseInvoice) =>
      mapExpenseInvoiceToBookingExpenseInvoiceLookup(expenseInvoice)
    )
    .filter((expenseInvoice): expenseInvoice is BookingExpenseInvoiceLookup => Boolean(expenseInvoice));

export const createBookingExpenseInvoiceDetailsLookupTrigger = <
  TTriggerResult extends ExpenseInvoiceDetailsTriggerResult,
>(
  trigger: (invoiceId: number) => TTriggerResult
): ((
  invoiceId: number
) => Omit<TTriggerResult, 'unwrap'> & {
  unwrap: () => Promise<BookingExpenseInvoiceDetailsLookup>;
}) =>
  (invoiceId: number) => {
    const triggerResult = trigger(invoiceId);

    return {
      ...triggerResult,
      unwrap: async (): Promise<BookingExpenseInvoiceDetailsLookup> => {
        const result = await triggerResult.unwrap();
        const mappedResult = mapExpenseInvoiceDetailsToBookingExpenseInvoiceDetailsLookup(result);

        if (!mappedResult) {
          throw new Error('Nie udało się pobrać szczegółów dokumentu kosztowego.');
        }

        return mappedResult;
      },
    };
  };

export const createSaveBookingExpenseInvoiceAssignmentsMutation = <TReturn>(
  mutate: (request: SaveBookingExpenseInvoiceAssignmentsRequest) => TReturn
): ((request: SaveBookingExpenseInvoiceAssignmentsRequest) => TReturn) =>
  (request: SaveBookingExpenseInvoiceAssignmentsRequest) =>
    mutate(buildSaveBookingExpenseInvoiceAssignmentsRequest(request));
